import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Box, Button, Card, Typography, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

// Sample questions
const questions = [
  {
    question: 'What interests you most about drones?',
    options: [
      'Capturing stunning aerial photos and videos.',
      'Using drones for commercial or industrial purposes.',
      'Participating in drone racing or competitions.',
      'Building and customizing drones.'
    ],
    image: require('../../assets/image/png/41343.jpg'), 
  },
  {
    question: 'What is your current level of drone knowledge?',
    options: [
      'Beginner – I\'ve never flown a drone.',
      'Intermediate – I\'ve flown drones occasionally.',
      'Advanced – I have experience and want to learn professional skills.'
    ],
    image: require('../../assets/image/png/4300_7_03.jpg'),
  },
  {
    question: 'What do you plan to achieve with drones?',
    options: [
      'Pursue a creative career in photography or videography.',
      'Use drones in industries like agriculture, mapping, or inspections.',
      'Enjoy drone flying as a recreational hobby.',
      'Start a drone-related business.'
    ],
    image: require('../../assets/image/png/333110-P9YIH7-617.jpg'),
  },
  {
    question: 'Which skill excites you the most?',
    options: [
      'Learning to fly drones like a pro.',
      'Capturing and editing breathtaking visuals.',
      'Understanding technical details and building drones.',
      'Learning how to use drones for problem-solving.'
    ],
    image: require('../../assets/image/png/daa9044a-4cff-4ec5-8eeb-940d347d1571.jpg'),
  },
  {
    question: 'How much time are you willing to dedicate to learning?',
    options: [
      'A few hours a week.',
      'Part-time over a few months.',
      'Full-time immersion.',
      'I\'m flexible, but I want to start immediately!'
    ],
    image: require('../../assets/image/png/41525eb4-0c95-4842-920b-7996b7b5ada2.jpg'),
  }
];

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  // MUI useMediaQuery hook for responsiveness
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // If the screen is small (mobile)

  const handleStart = () => {
    setCurrentStep(1);
  };

  const handleAnswer = (answer) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    setSelectedAnswer(answer);

    if (currentStep < questions.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setSelectedAnswer(answers[currentStep - 2] || null);
    }
  };

  return (
    <Box
  sx={{
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // backgroundColor: '#white',
    padding: 2,
  }}
>
  <Card
    sx={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row', // Stack vertically on mobile
      width: isMobile ? '100%' : '90%',
      maxWidth: '1200px',
      height: '90%',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
    }}
  >
    {/* Left Side: Question and Image */}
    <Box
      sx={{
        flex: 1,
        backgroundColor: '#1976d2',
        color: 'white',
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            {currentStep === 0 ? "Welcome to DroneShala" : questions[currentStep - 1].question}
          </Typography>
          {currentStep === 0 && (
            <Typography variant="body1" sx={{ mb: 4 }}>
              Let's discover your perfect drone journey! Take this quick quiz to get started.
            </Typography>
          )}
        </motion.div>
      </AnimatePresence>

      {currentStep === 0 ? (
        <Button
          onClick={handleStart}
          variant="contained"
          sx={{
            backgroundColor: 'white',
            color: '#1976d2',
            borderRadius: '50px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: '#f0f4f8',
            },
          }}
        >
          Start Quiz
          <ArrowRight style={{ marginLeft: 8 }} />
        </Button>
      ) : (
        <motion.img
          src={questions[currentStep - 1].image}
          alt="Drone"
          style={{
            width: '60%', // Shrink image size
            borderRadius: '8px',
            marginTop: '20px',
          }}
          initial={{ opacity: 0, scale: 1.2 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.5 }}
        />
      )}
    </Box>

    {/* Right Side: Options */}
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'white',
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflowY: 'auto',  // Allow scrolling
        paddingBottom: '20px',  // Add some padding to prevent content from touching the bottom
      }}
    >
      {/* Image with Animation */}
      <motion.img
        src={require('../../assets/image/png/QUESTION.png')} // Add your image path here
        alt="Question"
        style={{
          width: currentStep === 0 ? '80%' : '100%', // Full width for initial screen
          height: currentStep === 0 ? '100%' : 'auto', // Full height for initial screen
          maxWidth: currentStep === 0 ? 'none' : '190px', // No max width before quiz starts
          maxHeight: currentStep === 0 ? 'none' : '200px', // No max height before quiz starts
          marginBottom: '20px',
          objectFit: 'cover', // Ensures the image covers the space properly
        }}
        animate={{
          y: currentStep > 0 ? -50 : 0, // Move image up when options appear
          scale: currentStep > 0 ? 0.8 : 1, // Shrink image slightly
        }}
        transition={{ duration: 0.5 }}
      />

      {/* Options */}
      {currentStep > 0 && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <List
              sx={{
                width: '100%',
                maxHeight: 'auto', // Let it expand fully on mobile
                overflowY: 'auto', // Allow scrolling for options
                padding: 0,
                marginTop: '-80px',
                '& .MuiListItem-root': {
                  padding: '0', // Remove any extra padding
                },
              }}
            >
              {questions[currentStep - 1].options.map((option, index) => (
                <ListItem key={index} disablePadding>
                  <Button
                    fullWidth
                    onClick={() => handleAnswer(option)}
                    sx={{
                      padding: '8px', // Reduced padding for smaller view
                      marginBottom: 1, // Reduced margin
                      borderRadius: '12px',
                      backgroundColor: selectedAnswer === option ? '#1976d2' : '#f0f4f8',
                      color: selectedAnswer === option ? 'white' : 'black',
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      fontSize: '12px', // Smaller font size
                      fontWeight: 'medium',
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: selectedAnswer === option ? '#1565c0' : '#e0e7f1',
                      },
                    }}
                  >
                    {selectedAnswer === option && (
                      <CheckIcon sx={{ marginRight: 2, color: 'white' }} />
                    )}
                    <ListItemText primary={option} />
                  </Button>
                </ListItem>
              ))}
            </List>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Back Button */}
      {currentStep > 0 && (
        <Button
          onClick={handleBack}
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: '50px',
            padding: '10px 18px', // Reduced padding for smaller view
            fontSize: '14px', // Smaller font size
            fontWeight: 'bold',
            textTransform: 'none',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#f0f4f8',
              borderColor: '#1565c0',
            },
          }}
        >
          <ArrowLeft style={{ marginRight: 8 }} />
          Back
        </Button>
      )}
    </Box>
  </Card>
</Box>

  );
}

export default App;
